import {pushError} from '@js/actions/errorManagement';

import I18n from '@js/modules/translations';
import AnalyticsService from '@js/modules/analyticsService';

import Notification from '@js/components/layouts/notification';

const BLOB_URL_LIFETIME = 60000;

// Shared route-export flow: POST the drawn track, then trigger a browser download of the returned
// file. GPX / KML / TCX differ only in URL, Accept header, MIME type and default filename. Resolves
// to whether a file was downloaded (a quota spent on a refused export is given back).
const _exportRide = (track, distance, {path, accept, mime, fallbackName}) => {
    const formData = new FormData();

    formData.append('exporter[map_points]', track);
    formData.append('exporter[distance]', distance);

    const csrfToken = document.getElementsByName('csrf-token')[0];
    const token = csrfToken?.getAttribute('content');

    return _download(fetch(path, {
        credentials: 'same-origin',
        headers: new Headers({
            'X-CSRF-Token': token,
            Accept: accept
        }),
        method: 'POST',
        body: formData
    }), {mime, fallbackName});
};

// Downloads the file a request answers, or says why there is none. Resolves to whether a file came.
const _download = (request, {mime, fallbackName}) => {
    const action = `export_${fallbackName.split('.').pop()}`;

    return request
        .then(async (response) => {
            // A signed-out export is redirected (login): follow it, as the link did, rather than save the page.
            if (response.redirected) {
                window.location.href = response.url;

                return null;
            }

            // A refused export carries an error page, never a track: nothing to download.
            if (!response.ok) {
                AnalyticsService.trackActionFailed(action, `http_${response.status}`);
                Notification.error(I18n.t('js.helpers.errors.server'));

                return null;
            }

            return {
                filename: response.headers.get('content-disposition')?.split('filename=')[1]?.split(';')[0] || fallbackName,
                blob: await response.blob()
            };
        })
        .then((download) => {
            if (!download) {
                return false;
            }

            const {blob} = download;
            const filename = download.filename.replace(/^"|"$/g, '');

            const file = new File([blob], filename, {type: mime});
            const exportUrl = URL.createObjectURL(file);

            const anchor = document.createElement('a');
            anchor.href = exportUrl;
            anchor.download = filename;
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            // iOS Safari fetches the blob only once the user confirms its download prompt.
            setTimeout(() => URL.revokeObjectURL(exportUrl), BLOB_URL_LIFETIME);

            return true;
        })
        .catch((error) => {
            AnalyticsService.trackActionFailed(action, 'network');
            pushError(error);

            return false;
        });
};

const EXPORT_MIMES = {
    gpx: 'application/gpx+xml',
    kml: 'application/vnd.google-earth.kml+xml',
    tcx: 'application/vnd.garmin.tcx+xml'
};

// A stored ride's export (ride page): fetched rather than left to the link, to know whether a file came.
export const downloadExport = (url, format) => _download(fetch(url, {
    credentials: 'same-origin',
    headers: new Headers({Accept: EXPORT_MIMES[format]})
}), {mime: EXPORT_MIMES[format], fallbackName: `route.${format}`});

export const exportGPXRide = (track, distance) => _exportRide(track, distance, {
    path: '/api/v1/exporter/generate-gpx.gpx',
    accept: 'application/gpx+xml',
    mime: 'application/gpx+xml',
    fallbackName: 'route.gpx'
});

export const exportKMLRide = (track, distance) => _exportRide(track, distance, {
    path: '/api/v1/exporter/generate-kml.kml',
    accept: 'application/vnd.google-earth.kml+xml',
    mime: 'application/vnd.google-earth.kml+xml',
    fallbackName: 'route.kml'
});

export const exportTCXRide = (track, distance) => _exportRide(track, distance, {
    path: '/api/v1/exporter/generate-tcx.tcx',
    accept: 'application/vnd.garmin.tcx+xml',
    mime: 'application/vnd.garmin.tcx+xml',
    fallbackName: 'route.tcx'
});
